import {
  AVIATOR_BET_STEP,
  AVIATOR_BET_TIERS,
  AVIATOR_MAX_ODD,
  AVIATOR_MIN_BET,
  AVIATOR_MIN_ODD,
  isValidAviatorBet,
  isValidAviatorOdd,
} from '@hit-game/shared/constants';
import type { IAccountDataResponse, IAviatorProfile } from '@hit-game/shared/types';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '../common/Button';
import { FIELD_CLASS_BOLD, LABEL_CLASS } from '../common/form.styles';
import { Modal } from '../common/Modal';

interface AviatorConfigModalProps {
  account: IAccountDataResponse | null;
  profile: IAviatorProfile | null;
  busy: boolean;
  /** Tài khoản đang chạy Máy bay: vẫn sửa được, nhưng bản mới chỉ áp dụng từ ván sau. */
  running?: boolean;
  onClose: () => void;
  onSubmit: (input: {
    amount: number;
    odd: number;
    maxRounds: number;
    maxTotalStake: number;
  }) => void;
}

const vnd = (value: number): string => value.toLocaleString('vi-VN');

/**
 * Cấu hình chơi Aviator cho MỘT tài khoản.
 *
 * Hai ô bắt buộc (tiền cược + mức tự thoát) và hai trần ngân sách. Trần để `0`
 * là "không giới hạn" — hợp lệ, nhưng có cảnh báo: Aviator là cược trần một
 * hướng, EV âm, nên không có trần nghĩa là vòng lặp chạy tới khi hết sạch tiền.
 */
export function AviatorConfigModal({
  account,
  profile,
  busy,
  running = false,
  onClose,
  onSubmit,
}: AviatorConfigModalProps) {
  const [amount, setAmount] = useState('');
  const [odd, setOdd] = useState('');
  const [maxRounds, setMaxRounds] = useState('');
  const [maxTotalStake, setMaxTotalStake] = useState('');

  // Nạp lại mỗi lần mở cho một tài khoản KHÁC: giữ state cũ thì mở thẻ thứ hai
  // sẽ thấy số của thẻ thứ nhất và bấm Lưu là ghi đè bằng cấu hình của người khác.
  useEffect(() => {
    if (!account) return;
    setAmount(profile ? String(profile.amount) : String(AVIATOR_MIN_BET));
    setOdd(profile ? profile.odd.toFixed(2) : '1.20');
    setMaxRounds(profile ? String(profile.maxRounds) : '0');
    setMaxTotalStake(profile ? String(profile.maxTotalStake) : '0');
  }, [account, profile]);

  const amountNum = Number(amount);
  const oddNum = Number(odd);
  const roundsNum = Number(maxRounds);
  const stakeNum = Number(maxTotalStake);

  const amountError = isValidAviatorBet(amountNum)
    ? null
    : `Tối thiểu ${vnd(AVIATOR_MIN_BET)}, bội số ${vnd(AVIATOR_BET_STEP)}`;
  const oddError = isValidAviatorOdd(oddNum)
    ? null
    : `Trong khoảng ${String(AVIATOR_MIN_ODD)} – ${String(AVIATOR_MAX_ODD)}, tối đa 2 số lẻ`;
  const roundsError =
    Number.isInteger(roundsNum) && roundsNum >= 0 ? null : 'Số nguyên ≥ 0 (0 = không giới hạn)';
  const stakeError =
    Number.isInteger(stakeNum) && stakeNum >= 0 ? null : 'Số nguyên ≥ 0 (0 = không giới hạn)';

  const valid = !amountError && !oddError && !roundsError && !stakeError;
  const unlimited = roundsNum === 0 && stakeNum === 0;

  return (
    <Modal
      open={account !== null}
      onClose={onClose}
      busy={busy}
      maxWidth={520}
      title={`Cấu hình Máy bay — ${account?.username ?? ''}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Huỷ
          </Button>
          <Button
            onClick={() => {
              onSubmit({
                amount: amountNum,
                odd: oddNum,
                maxRounds: roundsNum,
                maxTotalStake: stakeNum,
              });
            }}
            disabled={!valid || busy}
            loading={busy}
          >
            Lưu
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {running && (
          <p className="border-line bg-panel text-ink-dim rounded-md border px-3 py-2 text-[11px]">
            Đang chạy — cấu hình mới áp dụng từ <b>ván sau</b>; ván đang đặt hoặc đang bay vẫn giữ
            số cũ.
          </p>
        )}
        <div>
          <label className={LABEL_CLASS} htmlFor="aviator-amount">
            Tiền cược mỗi ván
          </label>
          <input
            id="aviator-amount"
            type="number"
            step={AVIATOR_BET_STEP}
            min={AVIATOR_MIN_BET}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
            }}
            className={FIELD_CLASS_BOLD}
          />
          {/* Bốn mức nhanh y như trong game — gõ tay vẫn được, đây chỉ là phím tắt. */}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {AVIATOR_BET_TIERS.map((tier) => (
              <button
                key={tier}
                type="button"
                onClick={() => {
                  setAmount(String(tier));
                }}
                className={`rounded-md border px-2.5 py-1 text-[11px] font-bold transition-colors ${
                  amountNum === tier
                    ? 'border-accent-border bg-accent-soft text-accent'
                    : 'border-line bg-panel text-ink-dim hover:text-ink'
                }`}
              >
                {vnd(tier)}
              </button>
            ))}
          </div>
          {amountError && <p className="text-danger mt-1.5 text-[11px]">{amountError}</p>}
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="aviator-odd">
            Mức tự dừng (hệ số)
          </label>
          <input
            id="aviator-odd"
            type="number"
            step={0.01}
            min={AVIATOR_MIN_ODD}
            max={AVIATOR_MAX_ODD}
            value={odd}
            onChange={(event) => {
              setOdd(event.target.value);
            }}
            className={FIELD_CLASS_BOLD}
          />
          <p className="text-ink-faint mt-1.5 text-[11px]">
            Nhà cái tự chốt lãi ở đúng mốc này — bot không phải canh hệ số đang chạy.
          </p>
          {oddError && <p className="text-danger mt-1.5 text-[11px]">{oddError}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="aviator-max-rounds">
              Trần số ván
            </label>
            <input
              id="aviator-max-rounds"
              type="number"
              min={0}
              value={maxRounds}
              onChange={(event) => {
                setMaxRounds(event.target.value);
              }}
              className={FIELD_CLASS_BOLD}
            />
            {roundsError && <p className="text-danger mt-1.5 text-[11px]">{roundsError}</p>}
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="aviator-max-stake">
              Trần tổng cược
            </label>
            <input
              id="aviator-max-stake"
              type="number"
              min={0}
              value={maxTotalStake}
              onChange={(event) => {
                setMaxTotalStake(event.target.value);
              }}
              className={FIELD_CLASS_BOLD}
            />
            {stakeError && <p className="text-danger mt-1.5 text-[11px]">{stakeError}</p>}
          </div>
        </div>

        {unlimited && (
          <div className="border-warning/35 bg-warning/10 text-warning flex items-start gap-2 rounded-lg border px-3 py-2.5 text-[11px]">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              Cả hai trần đang để 0 — bot sẽ đặt cho tới khi hết tiền. Máy bay là cược một hướng,
              đặt trần ít nhất một trong hai.
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
